import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [planFilter, setPlanFilter] = useState('pro');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBilling();
  }, [planFilter]);

  const fetchBilling = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ plan: planFilter });
      const result = await apiGet(`${API_ROUTES.admin.billing}?${params}`);
      setData(result);
    } catch (e) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Billing & Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">Revenue tracking and subscriber management</p>
        </div>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="bg-dark-card border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-red-500/50"
        >
          <option value="pro">Pro Subscribers</option>
          <option value="free">Free Users</option>
          <option value="">All Users</option>
        </select>
      </div>

      {/* Revenue cards */}
      {data && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-400/10 to-green-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={16} className="text-green-400" />
              <span className="text-xs text-gray-500 uppercase">Monthly Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-400">${data.mrr}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-400/10 to-amber-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={16} className="text-amber-400" />
              <span className="text-xs text-gray-500 uppercase">Pro Subscribers</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{data.proCount}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-400/10 to-blue-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} className="text-blue-400" />
              <span className="text-xs text-gray-500 uppercase">Per User</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">$9<span className="text-sm text-gray-500">/mo</span></p>
          </div>
        </div>
      )}

      {/* Subscribers Table */}
      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Since</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : !data?.subscribers?.length ? (
                <tr><td colSpan={4} className="text-center py-12 text-gray-500">No subscribers found</td></tr>
              ) : (
                data.subscribers.map((s) => (
                  <tr key={s.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-gray-200 font-medium">{s.username}</p>
                      <p className="text-gray-500 text-xs">{s.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        s.plan === 'pro' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700/50 text-gray-400'
                      }`}>{s.plan}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded text-[10px] ${
                        s.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>{s.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.createdAt?.split('T')[0]}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
